import React from 'react';
import {
  getErrorMessage, useUserRole,
} from '@mairistem/finances-utils';
import { toast } from 'react-toastify';
import { Table } from 'rsuite-table';
import { newIdentifiant } from '@mairistem/tools';
import { useHistory } from 'react-router';
import TreeNodeType from '../../../constants/liquidation/TreeNodeType';
import type TreeViewType from '../../../constants/liquidation/TreeViewType';

import type SensOptions from '../../../interfaces/pieces/bordereaux/SensOptions';
import type { BordereauWithState, TreeRow } from '../../../interfaces/pieces/TreeRow';
import TreeViewFactory from '../../../classes/pieces/TreeViewFactory';
import LiquidationTreeviewLoader from './LiquidationTreeviewLoader';
import { getExercices } from '../../../services/entites/exercices';
import TreeRowFilter from '../../../classes/pieces/TreeRowFilter';
import BordereauStateType from '../../../constants/liquidation/bordereaux/BordereauStateType';
import TreeDataManager from '../../../classes/pieces/TreeDataManager';
import LiquidationEmptyResultNotifier from '../../../classes/pieces/LiquidationEmptyResultNotifier';

const collectStatesFromRows = (
  rows: TreeRow[],
  sensFilter: SensOptions,
): Set<BordereauStateType> => {
  const states = new Set<BordereauStateType>();

  const traverse = (list: TreeRow[]) => {
    list.forEach((row) => {
      if (row.nodeType === TreeNodeType.BORDEREAU && row.bordereau) {
        const matchesSens = sensFilter === 'T' || row.bordereau.sens === sensFilter;
        if (matchesSens) {
          states.add(row.bordereau.state.state);
        }
      }
      if (row.children?.length) {
        traverse(row.children);
      }
    });
  };

  traverse(rows);
  return states;
};

const cleanExpandedKeysFromEmptyEntities = (
  filteredRows: TreeRow[],
  expandedKeys: Set<string>,
): Set<string> => {
  const updatedKeys = new Set(expandedKeys);

  const checkRecursively = (rows: TreeRow[]) => {
    rows.forEach((row) => {
      if (
        row.nodeType === TreeNodeType.ENTITE
        && updatedKeys.has(row.rowKey)
        && row.children !== undefined
        && row.children.length === 0
      ) {
        updatedKeys.delete(row.rowKey);
      }

      if (row.children && row.children.length > 0) {
        checkRecursively(row.children);
      }
    });
  };

  checkRecursively(filteredRows);
  return updatedKeys;
};

const TABLE_LOCALE = { emptyMessage: 'Aucune donnée' };

interface PieceTreeViewProps {
  type: TreeViewType,
  height: number,
  filterSens: SensOptions,
  anneeExercice?: number,
  filtreBordereauState?: number,
  onVisibleStatesChange?: (states: Set<BordereauStateType>) => void;
}

const LiquidationTreeview = ({
  type,
  height,
  filterSens,
  anneeExercice,
  filtreBordereauState,
  onVisibleStatesChange,
}: PieceTreeViewProps) => {
  const [generalLoading, setGeneralLoading] = React.useState(false);
  const [data, setData] = React.useState<TreeRow[] | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<Set<string>>(new Set<string>());
  const [loadingRowKeys, setLoadingRowKeys] = React.useState<Set<string>>(new Set<string>());

  // Ne pas passer en global car si composant monté 2 fois
  // (ex: deux onglets différents) il partagerait le même set de notifications
  const emptyResultNotifier = React.useRef(new LiquidationEmptyResultNotifier());
  const dataRef = React.useRef<TreeRow[] | null>(null);

  React.useEffect(() => { dataRef.current = data; }, [data]);

  const dataTreeView = React.useMemo(
    () => TreeViewFactory.newInstance(type),
    [type],
  );

  const treeRowFilter = React.useMemo(
    () => new TreeRowFilter(filtreBordereauState, filterSens),
    [filtreBordereauState, filterSens],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const treeViewKey = React.useMemo(() => newIdentifiant(), [filtreBordereauState, filterSens]);

  const [userRole] = useUserRole();

  const history = useHistory();

  const filterData = React.useMemo(() => {
    if (data === null) return null;
    if (!data.length) return [];
    return treeRowFilter.apply(data);
  }, [data, treeRowFilter]);

  const expandedRowKeysArray = React.useMemo(
    () => Array.from(expandedRowKeys),
    [expandedRowKeys],
  );

  const loadEntites = React.useCallback(async (): Promise<void> => {
    setGeneralLoading(true);
    emptyResultNotifier.current.reset();
    try {
      const { data: exercices } = await getExercices(new URLSearchParams({
        anneeExercice: anneeExercice?.toString(),
        withEntite: 'true',
        isActif: 'true',
      }));
      const formattedEntites = exercices.map((exercice) => ({
        identifiant: exercice?.entite.identifiantEntite,
        libelle: exercice?.entite?.raisonSociale,
        rowKey: `entite-${exercice?.entite.identifiant}`,
        exercice,
        children: [],
        nodeType: TreeNodeType.ENTITE,
      }));
      setData(formattedEntites);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors du chargement des entités'));
    } finally {
      setGeneralLoading(false);
    }
  }, [anneeExercice]);

  const handleExpand = React.useCallback(
    async (isExpanded: boolean, rowData: TreeRow): Promise<void> => {
      if (!rowData || !rowData.rowKey) return;

      const { node: nodeInData } = TreeDataManager.findNode(data, rowData.rowKey);
      const needsLoading = !nodeInData?.children?.length;
      let loadedChildren: TreeRow[] | null = null;

      if (needsLoading) {
        setLoadingRowKeys((old) => new Set(old).add(rowData.rowKey));
        loadedChildren = await dataTreeView.loadData(rowData);
        setLoadingRowKeys((old) => {
          const newSet = new Set(old);
          newSet.delete(rowData.rowKey);
          return newSet;
        });

        setData((old) => TreeDataManager.updateNodeChildren(old, rowData.rowKey, loadedChildren));
      }

      // Permet de replier tous les mouvements quand on replie un borderau
      if (dataTreeView.isRowExpandable(rowData) && !isExpanded) {
        const currentNode = nodeInData ?? rowData;
        currentNode.children?.forEach((child) => {
          if (expandedRowKeys.has(`${child.rowKey}-details`)) {
            setExpandedRowKeys((old) => {
              const newSet = new Set(old);
              newSet.delete(`${child.rowKey}-details`);
              return newSet;
            });
          }
        });
      }

      const nextExpandedEntites = new Set(expandedRowKeys);
      if (nextExpandedEntites.has(rowData.rowKey)) {
        nextExpandedEntites.delete(rowData.rowKey);
      } else {
        nextExpandedEntites.add(rowData.rowKey);
      }
      setExpandedRowKeys(nextExpandedEntites);

      // Vérifie les résultats vides après expansion d'une entité
      if (isExpanded && rowData.nodeType === TreeNodeType.ENTITE) {
        const hasActiveFilters = filtreBordereauState !== null || filterSens !== 'T';
        if (hasActiveFilters) {
          // Utilise les children fraîchement chargés ou ceux déjà en mémoire
          const childrenToCheck = loadedChildren ?? nodeInData?.children ?? [];

          if (childrenToCheck.length > 0) {
            // Applique le filtre sur les children
            const filteredChildren = treeRowFilter.apply(childrenToCheck);

            if (filteredChildren.length === 0) {
              // Toujours afficher le toast lors du dépliage manuel
              toast.info('Aucun bordereau de ce budget ne correspond aux critères.');
              // Marque comme notifié pour éviter le doublon si les filtres changent juste après
              emptyResultNotifier.current.markNotified(rowData.rowKey, filtreBordereauState, filterSens);
            }
          }
        }
      }
    },
    [expandedRowKeys, dataTreeView, data, filterSens, filtreBordereauState, treeRowFilter],
  );

  const handleChangeRow = React.useCallback(
    async (rowData: TreeRow, shouldRefreshChildren?: boolean, newParent?: TreeRow) => {
      // Cas 1: Mise à jour avec un nouveau parent (déplacement vers un autre bordereau)
      if (newParent) {
        const updatedParent = { ...newParent };

        if (shouldRefreshChildren) {
          const children = await dataTreeView.loadData(newParent);
          updatedParent.children = dataTreeView.orderData(children, updatedParent.nodeType);
        }

        setData((old) => {
          const { data: updated } = TreeDataManager.updateNode(old, newParent.rowKey, () => updatedParent);
          return updated;
        });
        return;
      }

      // Cas 2: Mise à jour simple ou avec rafraîchissement des children
      const updatedRow = { ...rowData };

      if (shouldRefreshChildren) {
        const children = await dataTreeView.loadData(rowData);
        updatedRow.children = dataTreeView.orderData(children, updatedRow.nodeType);
      }

      // Cas 3: Mise à jour du montant du bordereau parent quand on modifie un mouvement
      if (rowData?.mouvement && rowData?.bordereau?.montant) {
        setData((old) => {
          const parentInData = TreeDataManager.getParentFromData(old, rowData.rowKey);
          if (parentInData?.bordereau) {
            // D'abord mettre à jour le montant du bordereau parent
            let updated = TreeDataManager.updateNode(old, parentInData.rowKey, (node) => ({
              ...node,
              bordereau: {
                ...node.bordereau,
                montant: rowData.bordereau.montant,
              },
            })).data;

            updated = TreeDataManager.updateNode(updated, rowData.rowKey, () => updatedRow).data;
            return updated;
          }
          return old;
        });
        return;
      }

      // Mise à jour standard
      setData((old) => {
        const { data: updated } = TreeDataManager.updateNode(old, rowData.rowKey, () => updatedRow);
        return updated;
      });
    },
    [dataTreeView],
  );

  const handleDeleteRow = React.useCallback((rowData: TreeRow): void => {
    setData((old) => TreeDataManager.deleteNode(old, rowData.rowKey));
  }, []);

  const handleAddRow = React.useCallback((parent: TreeRow): void => {
    setData((old) => {
      const { data: updated } = TreeDataManager.updateNode(old, parent.rowKey, (node) => ({
        ...node,
        children: dataTreeView.orderData(parent.children ?? [], node.nodeType),
      }));
      return updated;
    });
  }, [dataTreeView]);

  const toggleExpand = React.useCallback((rowKey: string) => {
    setExpandedRowKeys((prev) => {
      const nextToggleExpandItems = new Set(prev);
      if (nextToggleExpandItems.has(rowKey)) {
        nextToggleExpandItems.delete(rowKey);
      } else {
        nextToggleExpandItems.add(rowKey);
      }
      return nextToggleExpandItems;
    });
  }, []);

  const handleClick = React.useCallback((rowData: TreeRow): void => {
    if (rowData.nodeType === TreeNodeType.MOUVEMENT) {
      setExpandedRowKeys((prev) => {
        const newSet = new Set<string>();
        let isAlreadyExpanded = false;

        // eslint-disable-next-line no-restricted-syntax
        for (const key of prev) {
          if (key.startsWith('mouvement-')) {
            if (key === rowData.rowKey) {
              isAlreadyExpanded = true;
            }
            // eslint-disable-next-line no-continue
            continue;
          }
          newSet.add(key);
        }

        if (!isAlreadyExpanded) {
          newSet.add(rowData.rowKey);
        }

        return newSet;
      });
    } else {
      toggleExpand(rowData.rowKey);
    }
  }, [toggleExpand]);

  const handleDoubleClick = React.useCallback((rowData: TreeRow): void => {
    if (rowData.nodeType !== TreeNodeType.MOUVEMENT) return;

    const parentRow = TreeDataManager.getParentFromData(dataRef.current, rowData.rowKey);
    const parentBordereau: BordereauWithState = parentRow?.bordereau;

    if (parentBordereau?.state?.state === BordereauStateType.EN_COURS) {
      history.push(`/liquidations/${rowData.identifiant}`);
    } else {
      history.push(`/liquidations/${rowData.identifiant}`, { readonly: true });
    }
  }, [history]);

  React.useEffect(() => {
    if (!anneeExercice) return;
    setExpandedRowKeys(new Set());
    loadEntites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadEntites, anneeExercice]);

  React.useEffect(() => {
    if (!data?.length || !filterData) return;

    const cleanedKeys = cleanExpandedKeysFromEmptyEntities(filterData, expandedRowKeys);

    if (cleanedKeys.size !== expandedRowKeys.size) {
      setExpandedRowKeys(cleanedKeys);
    }

    const shouldNotify = emptyResultNotifier.current.notifyEntities(
      filterData,
      data,
      cleanedKeys,
      filtreBordereauState,
      filterSens,
    );

    if (shouldNotify) {
      toast.info('Aucun bordereau de ce budget ne correspond aux critères.');
    }
  }, [filterData, data, expandedRowKeys, filtreBordereauState, filterSens]);

  React.useEffect(() => {
    const states = collectStatesFromRows(data ?? [], filterSens);
    onVisibleStatesChange?.(states);
  }, [data, filterSens, onVisibleStatesChange]);

  const renderOptions = React.useMemo(
    () => ({ filterSens, data }),
    [filterSens, data],
  );

  const renderTreeToggle = React.useCallback(
    (_expandButton: React.ReactNode, rowData: TreeRow, isExpanded: boolean) => (
      dataTreeView.renderTreeToggle(isExpanded, loadingRowKeys.has(rowData.rowKey))
    ),
    [dataTreeView, loadingRowKeys],
  );

  const getRowHeight = React.useCallback(
    (rowData: TreeRow) => dataTreeView?.getRowHeight(rowData, expandedRowKeys.has(rowData?.rowKey)),
    [dataTreeView, expandedRowKeys],
  );

  if (generalLoading || data === null || filterData === null) {
    return <LiquidationTreeviewLoader />;
  }

  return (
    <Table
      key={treeViewKey}
      isTree
      locale={TABLE_LOCALE}
      rowKey="rowKey"
      hover={false}
      data={filterData}
      expandedRowKeys={expandedRowKeysArray}
      className="liquidation-treeview"
      loading={generalLoading}
      rowExpandedHeight={500}
      height={height - 150}
      virtualized
      showHeader={false}
      renderTreeToggle={renderTreeToggle}
      shouldUpdateScroll={false}
      rowHeight={getRowHeight}
      onExpandChange={handleExpand}
    >
      {({ Cell, Column, HeaderCell }) => (
        <Column flexGrow={1} verticalAlign="middle">
          <HeaderCell>
            {' '}
          </HeaderCell>
          <Cell>
            {(rowData: TreeRow) => (dataTreeView?.renderRow(
              rowData,
              expandedRowKeys.has(rowData.rowKey),
              userRole,
              () => handleClick(rowData),
              () => handleDoubleClick(rowData),
              handleChangeRow,
              handleDeleteRow,
              handleAddRow,
              renderOptions,
            ))}
          </Cell>
        </Column>
      )}
    </Table>
  );
};

export default LiquidationTreeview;
